import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bot, HardDrive, Clock, FileText, FolderOpen, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Spinner } from '../components/Common';
import * as api from '../api';
import { AgentLog, Workspace } from '../types';

export function HomePage() {
  const navigate = useNavigate();

  const workspacesQuery = useQuery({
    queryKey: ['workspaces'],
    queryFn: api.getWorkspaces,
  });

  const logsQuery = useQuery({
    queryKey: ['logs', { limit: 10 }],
    queryFn: () => api.getLogs({ limit: 10 }),
  });

  const workspaces = workspacesQuery.data || [];
  const recentLogs = logsQuery.data || [];

  if (workspacesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Welcome to Workspace Studio</h1>
        <p className="text-gray-500 mt-1">Manage your agent workspaces and files</p>
      </div>

      {/* Workspaces Grid */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Workspaces</h2>
        </div>

        {workspaces.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-12 text-center">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No workspaces yet</h3>
            <p className="text-gray-500 mb-4">
              Create your first workspace to start organizing files
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {workspaces.map((workspace: Workspace) => (
              <button
                key={workspace.name}
                onClick={() => navigate(`/workspace/${workspace.name}`)}
                className="bg-white border rounded-xl p-4 text-left hover:shadow-md hover:border-primary-200 transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                    <HardDrive className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{workspace.name}</h3>
                    <p className="text-sm text-gray-500 truncate">
                      {workspace.description || 'No description'}
                    </p>
                    {workspace.totalFiles !== undefined && (
                      <p className="text-xs text-gray-400 mt-1">
                        {workspace.totalFiles} files
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
          <button
            onClick={() => navigate('/activity')}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            View all
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {logsQuery.isLoading ? (
          <div className="bg-white border rounded-xl p-8">
            <Spinner />
          </div>
        ) : recentLogs.length === 0 ? (
          <div className="bg-white border rounded-xl p-8 text-center text-gray-500">
            <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="bg-white border rounded-xl overflow-hidden">
            {recentLogs.slice(0, 5).map((log: AgentLog, index: number) => (
              <div
                key={log.id}
                className={`flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors ${
                  index !== 0 ? 'border-t' : ''
                }`}
              >
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{log.agentId}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      {log.operation.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">
                    <span className="text-gray-400">{log.workspace}</span>
                    <span className="mx-1">/</span>
                    <span className="font-mono text-xs">{log.path}</span>
                  </p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
